/**
 * Excel Import Utility for PolicyTrackr
 * Parses Excel files, maps columns via synonym engine, and creates client + policy records in Firestore.
 */
import * as XLSX from 'xlsx';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { parseDate, calculatePolicyStatus } from './dateUtils';
import {
    COMPANIES, VEHICLE_MAKES, POLICY_TYPES,
    getCompaniesByCategory, getVehicleMakesByType, getPolicyTypesByCategory
} from '../data/dropdowns';

// ============================================================
// Column Mappings — each field has multiple synonym headers
// ============================================================

export const COLUMN_SYNONYMS = {
    customerName: [
        "Customer Name",
        "Proposer Name",
        "Client Name",
        "Owner Name",
        "Insured Name",
        "Name"
    ],

    customerMobile: [
        "Mobile",
        "Mobile Number",
        "Contact Number",
        "Phone",
        "Phone Number",
        "Customer Mobile"
    ],

    customerAddress: [
        "Address",
        "Customer Address",
        "Client Address"
    ],

    vehicleNumber: [
        "Vehicle Number",
        "Vehicle No",
        "Registration Number",
        "Reg Number"
    ],

    make: [
        "Make",
        "Vehicle Make"
    ],

    model: [
        "Model",
        "Vehicle Model"
    ],

    policyType: [
        "Policy Type",
        "Type"
    ],

    company: [
        "Company",
        "Insurer",
        "Insurance Company"
    ],

    policyNumber: [
        "Policy Number",
        "Policy No",
        "Policy #"
    ],

    expiryDate: [
        "Expiry Date",
        "Policy Expiry",
        "Expiry"
    ],

    netPremium: [
        "Net Premium",
        "Premium"
    ],

    totalPremium: [
        "Total Premium",
        "Gross Premium"
    ],

    amountReceived: [
        "Amount Received",
        "Paid Amount",
        "Received Amount"
    ],

    balancePending: [
        "Balance",
        "Balance Amount",
        "Pending Amount",
        "Due Amount",
        "Outstanding Amount",
        "Remaining Amount",
        "Balance Pending"
    ],

    sumInsured: [
        "Sum Insured",
        "Sum insured",
        "Insured Amount",
        "Insured",
        "Insured Value"
    ],

    category: [
        "Category",
        "Policy Category"
    ],

    vehicleType: [
        "Vehicle Type",
        "Type of Vehicle",
        "Type"
    ],
};

// Combined fields list for auto-detection
const ALL_FIELDS = [
    'customerName', 'customerMobile', 'customerAddress',
    'vehicleType', 'vehicleNumber', 'make', 'model',
    'category', 'policyType', 'company', 'policyNumber', 'expiryDate',
    'netPremium', 'totalPremium', 'amountReceived', 'balancePending', 'sumInsured'
];

// ============================================================
// Constants
// ============================================================
export const MAX_ROWS = 500;

// ============================================================
// Helpers
// ============================================================

/** Normalise a header string for comparison: lowercase, trim, collapse whitespace */
const normalizeHeader = (h) => String(h).toLowerCase().trim().replace(/\s+/g, ' ');

/** Clean numeric value — handles commas, ₹ symbol, etc. */
const toNumber = (val) => {
    if (val == null || val === '') return 0;
    if (typeof val === 'number') return val;
    const cleaned = String(val).replace(/[^0-9.-]/g, '');
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
};

/** Clean string value */
const toStr = (val) => {
    if (val == null) return '';
    return String(val).trim();
};

/** Clean mobile number — keep digits only, strip leading 91 if 12 digits */
const cleanMobile = (val) => {
    if (val == null) return '';
    let mobile = String(val).replace(/[^0-9]/g, '');
    if (mobile.length === 12 && mobile.startsWith('91')) {
        mobile = mobile.slice(2);
    }
    return mobile;
};

/**
 * Match an imported value against a list of valid dropdown options.
 * Uses case-insensitive comparison and substring matching as fallback.
 * @param {string} rawValue - the value from the Excel cell
 * @param {string[]} options - the valid dropdown options
 * @returns {string} - the matched dropdown value, or the original value if no match
 */
const matchDropdownValue = (rawValue, options) => {
    if (!rawValue || !options || options.length === 0) return rawValue || '';
    const norm = String(rawValue).trim().toLowerCase();
    if (!norm) return '';

    // 1. Exact case-insensitive match
    for (const opt of options) {
        if (opt.toLowerCase() === norm) return opt;
    }

    // 2. "Contains" match — the dropdown option contains the input or vice-versa
    for (const opt of options) {
        const optLower = opt.toLowerCase();
        if (optLower.includes(norm) || norm.includes(optLower)) return opt;
    }

    // 3. No match — return original
    return rawValue.trim();
};

/**
 * Detect vehicleType from the make value by checking which vehicle-make list contains it.
 * @param {string} make - the matched make value
 * @returns {string} - 'Two Wheeler', 'Private Vehicle', 'Commercial Vehicle' or ''
 */
const detectVehicleType = (make) => {
    if (!make) return '';
    const makeLower = make.toLowerCase();
    if (VEHICLE_MAKES.two_wheeler.some(m => m.toLowerCase() === makeLower)) return 'Two Wheeler';
    if (VEHICLE_MAKES.four_wheeler.some(m => m.toLowerCase() === makeLower)) return 'Private Vehicle';
    if (VEHICLE_MAKES.commercial_vehicle.some(m => m.toLowerCase() === makeLower)) return 'Commercial Vehicle';
    return '';
};

// ============================================================
// Header Mapping Engine
// ============================================================

/**
 * Build a lookup map from normalised Excel header → internal field name.
 * Runs once per import to auto-detect which headers map to which fields.
 *
 * @param {string[]} excelHeaders - the raw header names from the Excel sheet
 * @param {string[]} fieldList - which fields to look for (MOTOR_FIELDS or NON_MOTOR_FIELDS)
 * @returns {{ headerMap: Object<string,string>, unmappedHeaders: string[] }}
 *   headerMap: { "original excel header" → "internalFieldName" }
 */
export const buildHeaderMap = (excelHeaders, fieldList) => {
    const headerMap = {};           // originalHeader → fieldName
    const mappedFields = new Set(); // track which fields have been matched
    const unmappedHeaders = [];

    for (const originalHeader of excelHeaders) {
        const norm = normalizeHeader(originalHeader);
        let matched = false;

        for (const fieldName of fieldList) {
            if (mappedFields.has(fieldName)) continue; // already matched
            const synonyms = COLUMN_SYNONYMS[fieldName] || [];

            for (const synonym of synonyms) {
                if (normalizeHeader(synonym) === norm) {
                    headerMap[originalHeader] = fieldName;
                    mappedFields.add(fieldName);
                    matched = true;
                    break;
                }
            }
            if (matched) break;
        }

        if (!matched) {
            unmappedHeaders.push(originalHeader);
        }
    }

    return { headerMap, unmappedHeaders, mappedFields: [...mappedFields] };
};

/**
 * Resolve a field value from a row using the pre-built headerMap.
 * @param {Object} row - the Excel row
 * @param {string} fieldName - the internal field name (e.g. "customerName")
 * @param {Object} reverseMap - { fieldName → originalExcelHeader }
 */
const resolveField = (row, fieldName, reverseMap) => {
    const originalHeader = reverseMap[fieldName];
    if (!originalHeader) return undefined;
    const val = row[originalHeader];
    if (val === undefined || val === null || val === '') return undefined;
    return val;
};

// ============================================================
// Parse Excel File
// ============================================================

/**
 * Read an Excel File object and return rows as an array of objects plus headers.
 * @param {File} file
 * @returns {Promise<{ rows: Object[], headers: string[] }>}
 */
export const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
                // Extract headers from the first row's keys
                const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
                resolve({ rows, headers });
            } catch (err) {
                reject(new Error('Failed to parse Excel file: ' + err.message));
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
};

// ============================================================
// Map a single row to a policy record
// ============================================================

/**
 * @param {Object} row – raw Excel row
 * @param {Object} reverseMap – { fieldName → originalExcelHeader }
 * @returns {{ clientData, policyData, errors[] }}
 */
export const mapRowToRecord = (row, reverseMap) => {
    const errors = [];

    // ---- Client fields ----
    const customerName = toStr(resolveField(row, 'customerName', reverseMap)).toUpperCase();
    const customerMobile = cleanMobile(resolveField(row, 'customerMobile', reverseMap));
    const customerAddress = toStr(resolveField(row, 'customerAddress', reverseMap)).toUpperCase();

    if (!customerName) errors.push('Missing Customer Name');
    if (!customerMobile || customerMobile.length < 10) errors.push('Invalid Customer Mobile');

    // ---- Extraction of potentially relevant fields for category detection ----
    const rawPolicyType = toStr(resolveField(row, 'policyType', reverseMap));
    const rawCategory = toStr(resolveField(row, 'category', reverseMap));
    const rawVehicleType = toStr(resolveField(row, 'vehicleType', reverseMap));
    const rawVehicleNumber = toStr(resolveField(row, 'vehicleNumber', reverseMap));
    const rawMake = toStr(resolveField(row, 'make', reverseMap));
    const rawModel = toStr(resolveField(row, 'model', reverseMap));

    // ---- Category Auto-Detection ----
    let detectedCategory = 'non-motor'; // default
    let detectedCategoryType = 'health'; // default type

    const vehicleTypeOptions = ['Two Wheeler', 'Private Vehicle', 'Commercial Vehicle'];
    const matchedVehicleType = matchDropdownValue(rawVehicleType, vehicleTypeOptions);

    // 1. Motor Check: Has vehicle number, matched vehicle type, or specific makes
    if (rawVehicleNumber || matchedVehicleType || (rawMake && detectVehicleType(rawMake))) {
        detectedCategory = 'motor';
    }
    // 2. Life Check: Category or Policy Type contains "life"
    else if (
        rawCategory.toLowerCase().includes('life') ||
        rawPolicyType.toLowerCase().includes('life') ||
        rawPolicyType.toLowerCase().includes('term insurance')
    ) {
        detectedCategory = 'life';
        detectedCategoryType = 'life';
    }
    // 3. Health Check
    else if (
        rawCategory.toLowerCase().includes('health') ||
        rawCategory.toLowerCase().includes('medical') ||
        rawPolicyType.toLowerCase().includes('health') ||
        rawPolicyType.toLowerCase().includes('mediclaim')
    ) {
        detectedCategory = 'non-motor';
        detectedCategoryType = 'health';
    }

    // ---- Common policy fields (with dropdown matching based on detected category) ----
    const categoryForLists = detectedCategory === 'motor' ? 'motor' : (detectedCategoryType === 'life' ? 'life' : 'health');
    const companyList = getCompaniesByCategory(categoryForLists);
    const policyTypeList = getPolicyTypesByCategory(categoryForLists);

    const policyTypeVal = matchDropdownValue(rawPolicyType, policyTypeList);
    const company = matchDropdownValue(toStr(resolveField(row, 'company', reverseMap)), companyList);
    const policyNumber = toStr(resolveField(row, 'policyNumber', reverseMap)).toUpperCase();
    const totalPremium = toNumber(resolveField(row, 'totalPremium', reverseMap));
    const amountReceived = toNumber(resolveField(row, 'amountReceived', reverseMap));
    const balancePending = toNumber(resolveField(row, 'balancePending', reverseMap));

    // Expiry date
    const rawExpiry = resolveField(row, 'expiryDate', reverseMap);
    let expiryDateStr = '';
    if (rawExpiry) {
        const parsed = parseDate(rawExpiry);
        if (parsed && !isNaN(parsed.getTime())) {
            const dd = String(parsed.getDate()).padStart(2, '0');
            const mm = String(parsed.getMonth() + 1).padStart(2, '0');
            const yyyy = parsed.getFullYear();
            expiryDateStr = `${dd}-${mm}-${yyyy}`;
        } else if (typeof rawExpiry === 'string') {
            expiryDateStr = rawExpiry.trim();
        }
    }

    if (!policyNumber) errors.push('Missing Policy Number');

    // ---- Status auto-calculation ----
    const status = calculatePolicyStatus(expiryDateStr);

    // ---- Build the policy data ----
    let policyData = {};

    if (detectedCategory === 'motor') {
        const vehicleType = matchedVehicleType || (rawMake ? detectVehicleType(rawMake) : 'Two Wheeler');

        // Get the make list for refinement
        const allMakes = [
            ...VEHICLE_MAKES.two_wheeler,
            ...VEHICLE_MAKES.four_wheeler,
            ...VEHICLE_MAKES.commercial_vehicle
        ];
        const makesForType = vehicleType ? getVehicleMakesByType(vehicleType) : allMakes;
        const make = matchDropdownValue(rawMake, makesForType.length > 0 ? makesForType : allMakes);

        policyData = {
            name: customerName,
            mobile: customerMobile,
            address: customerAddress,
            vehicleType,
            vehicleNumber: rawVehicleNumber.toUpperCase(),
            vehicleMake: make,
            vehicleModel: rawModel.toUpperCase(),
            policyType: policyTypeVal,
            policyCompany: company,
            policyNumber,
            expiryDate: expiryDateStr,
            netPremium: String(toNumber(resolveField(row, 'netPremium', reverseMap))),
            premiumAmount: String(totalPremium),
            totalAmount: String(totalPremium),
            paidAmount: String(amountReceived),
            receivedAmount: String(amountReceived),
            amountPayable: String(totalPremium),
            balancePending: String(balancePending),
            policyCategory: 'motor',
            policyCategoryType: 'vehicle',
            status,
        };
    } else {
        // non-motor or life
        const sumInsured = toNumber(resolveField(row, 'sumInsured', reverseMap));

        policyData = {
            name: customerName,
            mobile: customerMobile,
            address: customerAddress,
            category: rawCategory.toUpperCase() || detectedCategoryType.toUpperCase(),
            policyType: policyTypeVal,
            policyCompany: company,
            policyNumber,
            expiryDate: expiryDateStr,
            sumInsured: String(sumInsured),
            premiumAmount: String(totalPremium),
            totalAmount: String(totalPremium),
            paidAmount: String(amountReceived),
            receivedAmount: String(amountReceived),
            amountPayable: String(totalPremium),
            balancePending: String(balancePending),
            policyCategory: detectedCategory,
            policyCategoryType: detectedCategoryType,
            status,
        };
    }

    const clientData = {
        name: customerName,
        mobile: customerMobile,
        address: customerAddress,
    };

    return { clientData, policyData, errors };
};

// ============================================================
// Process entire import — batch write to Firestore
// ============================================================

/**
 * @param {Object[]} rows - parsed Excel rows
 * @param {string[]} headers - raw Excel headers
 * @param {string} agentId - the UID of the user who owns the data
 * @param {Object} db - Firestore db instance
 * @param {Function} onProgress - callback(status, current, total)
 * @returns {Promise<{ total, success, failed, errors[], mappedFields[], unmappedHeaders[] }>}
 */
export const processImport = async (rows, headers, agentId, db, onProgress) => {
    const total = rows.length;
    let success = 0;
    let failed = 0;
    const importErrors = [];

    // 1. Build Header Map
    onProgress?.('Mapping columns', 0, total);
    const { headerMap, unmappedHeaders, mappedFields } = buildHeaderMap(headers, ALL_FIELDS);
    const reverseMap = {};
    Object.entries(headerMap).forEach(([h, f]) => reverseMap[f] = h);

    // Validation
    if (!reverseMap.customerName || !reverseMap.customerMobile || !reverseMap.policyNumber) {
        const missing = [];
        if (!reverseMap.customerName) missing.push('Customer Name');
        if (!reverseMap.customerMobile) missing.push('Customer Mobile');
        if (!reverseMap.policyNumber) missing.push('Policy Number');
        throw new Error(`Missing required columns: ${missing.join(', ')}`);
    }

    // 2. Pre-fetch existing data
    onProgress?.('Reading existing data', 0, total);

    // Existing Clients (by Name + Mobile composite key)
    const clientCache = new Map();
    try {
        const customersRef = collection(db, 'agents', agentId, 'customers');
        const snapshot = await getDocs(customersRef);
        snapshot.docs.forEach((d) => {
            const data = d.data();
            if (data.name && data.mobile) {
                const key = `${data.name.toUpperCase().trim()}_${data.mobile}`;
                clientCache.set(key, d.id);
            }
        });
    } catch (err) {
        console.warn('Could not pre-fetch clients:', err);
    }

    // Existing Policy Numbers (to avoid duplicates in this agent's scope)
    const existingPolicyNumbers = new Set();
    try {
        const policiesRef = collection(db, 'agents', agentId, 'policies');
        const policySnapshot = await getDocs(policiesRef);
        policySnapshot.docs.forEach(d => {
            const pNum = d.data().policyNumber;
            if (pNum) existingPolicyNumbers.add(pNum.toUpperCase().trim());
        });
    } catch (err) {
        console.warn('Could not pre-fetch policies:', err);
    }

    // 3. Process records in batches
    onProgress?.('Processing records', 0, total);
    const BATCH_SIZE = 400;
    let batch = writeBatch(db);
    let batchOps = 0;

    const commitBatch = async () => {
        if (batchOps > 0) {
            await batch.commit();
            batch = writeBatch(db);
            batchOps = 0;
        }
    };

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNum = i + 2; // +2 for 1-indexed + header row

        try {
            const { clientData, policyData, errors } = mapRowToRecord(row, reverseMap);

            if (errors.length > 0) {
                failed++;
                importErrors.push({ row: rowNum, errors });
                continue;
            }

            // ---- Duplicate Policy Check ----
            const pNumKey = policyData.policyNumber.toUpperCase().trim();
            if (existingPolicyNumbers.has(pNumKey)) {
                failed++;
                importErrors.push({
                    row: rowNum,
                    errors: [`Policy ${policyData.policyNumber} already exists`]
                });
                continue;
            }
            existingPolicyNumbers.add(pNumKey); // Add to set so we don't import duplicates within the same sheet

            // ---- Client: find or create (using composite key) ----
            const clientKey = `${clientData.name.toUpperCase().trim()}_${clientData.mobile}`;
            let customerId = clientCache.get(clientKey);

            if (!customerId) {
                customerId = uuidv4();
                const clientRef = doc(db, 'agents', agentId, 'customers', customerId);
                batch.set(clientRef, {
                    ...clientData,
                    email: '',
                    referredBy: '',
                    createdBy: agentId,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    agentId,
                    status: 'Customer',
                });
                batchOps++;
                clientCache.set(clientKey, customerId);
            }

            // ---- Policy: create at top-level agents/{agentId}/policies ----
            const policyId = uuidv4();
            const policyRef = doc(db, 'agents', agentId, 'policies', policyId);

            batch.set(policyRef, {
                ...policyData,
                customerId,
                agentId,
                createdBy: agentId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                status: policyData.status || 'Active', // Ensure status is set
                pdfUrl: '',
                pdfSource: '',
                driveFileId: null,
                firebaseUrl: null,
                email: '',
                referredBy: '',
                discount: '0',
                modeOfPayment: '',
                remarks: 'Imported from Excel',
            });
            batchOps++;
            success++;

            // Periodically commit and update progress
            if (batchOps >= BATCH_SIZE) {
                await commitBatch();
                onProgress?.('Saving records', i + 1, total);
            } else {
                onProgress?.('Processing records', i + 1, total);
            }

        } catch (err) {
            console.error(`Error at row ${rowNum}:`, err);
            failed++;
            importErrors.push({ row: rowNum, errors: [err.message || 'Unknown processing error'] });
        }
    }

    // Final commit
    onProgress?.('Finishing import', total, total);
    await commitBatch();

    return {
        total,
        success,
        failed,
        errors: importErrors,
        mappedFields,
        unmappedHeaders
    };
};
